import { Button, Card, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { Action } from '@/components/shared/ListSelector/helper';
import { ProductPromotionsModal } from '@/pages/Product/ProductPromotionsModal';
import { ApiProduct, ApiPromotion } from '@api/interfaces';
import LocalOffer from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Stack, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProductCardProps {
  disabled?: boolean,
  onAction: (action: Action) => void,
  product: ApiProduct,
  promotions: ApiPromotion[],
  selectedIds: string[],
  unavailable?: boolean,
}

export const ProductCard = ({
  disabled,
  onAction,
  product,
  promotions,
  selectedIds,
  unavailable,
}: ProductCardProps) => {
  const theme = useTheme();
  const isProductAdded = selectedIds.includes(product.id);
  const { t } = useTranslation();
  const [showViewProductPromotionsModal, setShowViewProductPromotionsModal] = useState(false);
  return (
    <Card testID={`product-${product.id}-card`}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack>
          {!!product.manufacturer?.name && (
            <>
              <Text
                category="overline"
                sx={{ color: theme.palette.text.secondary }}
                testID="product-manufacturer-name"
              >
                {product.manufacturer.name.toUpperCase()}
              </Text>
              <VSpacer size="1" />
            </>
          )}
          <Text category="body-large">{product.name}</Text>
          <VSpacer size="1" />
          <Text
            category="body-medium"
            sx={{ color: theme.palette.text.secondary }}
            testID="product-id"
          >
            {product.externalId
              ? t($ => $.products.erpId, { id: product.externalId })
              : t($ => $.products.id, { id: product.id })}
          </Text>
        </Stack>
        <Stack alignItems="center" direction="row" gap="24px">
          {!!promotions.length && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowViewProductPromotionsModal(true);
              }}
              size="small"
              sx={{
                backgroundColor: theme.palette.rewardsYellow,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.palette.rewardsYellow,
                },
              }}
              testID="rewards-button"
            >
              <LocalOffer
                sx={{ color: theme.palette.text.primary, height: '18px', width: '18px' }}
              />
              <HSpacer size="2"/>
              <Text category="label-large" sx={{ color: theme.palette.text.primary }}>
                {t($ => $.products.viewPromotions)}
              </Text>
            </Button>
          )}
          {unavailable && (
            <Text>
              Product unavailable
            </Text>
          )}
          {!unavailable && isProductAdded && (
            <Button
              disabled={disabled}
              onClick={() => onAction(Action.RemoveAll)}
              startIcon={<DeleteOutlineIcon/>}
              testID={`product-${product.id}-remove-button`}
              variant="text"
            >
              Remove
            </Button>
          )}
          {!unavailable && !isProductAdded && (
            <Button
              disabled={disabled}
              onClick={() => onAction(Action.AddAll)}
              startIcon={<AddIcon/>}
              testID={`product-${product.id}-add-button`}
              variant="outlined"
            >
              Add
            </Button>
          )}
        </Stack>
      </Stack>
      {showViewProductPromotionsModal && (
        <ProductPromotionsModal
          onClose={() => setShowViewProductPromotionsModal(false)}
          promotions={promotions}
        />
      )}
    </Card>
  );
};
